import { AnimatePresence, m } from 'motion/react';
import { useEffect, useState } from 'react';
import { useInterval, useThrottle, useWindowSize } from 'react-use';
import { useHeaderStore } from 'stores/header';

interface WrapperDesktopProps {
  children: React.ReactNode;
  isOpen: boolean;
  onClose: () => void;
  iconRef: React.RefObject<HTMLElement>;
}

/**
 * Wrapper for desktop Subscription confirmation information
 */
const WrapperDesktop: React.FC<WrapperDesktopProps> = ({ children, isOpen, onClose, iconRef }) => {
  const [toolTipDom, setToolTipDom] = useState<DOMRect | null>(null);
  const [isRunning, toggleIsRunning] = useState(true);
  const windowSize = useWindowSize();
  const { isHeaderHidden } = useHeaderStore();
  // Wait until header entry animation is completed
  const throttledIsOpen = useThrottle(isOpen, 1000);
  const [mouseEnter, setMouseEnter] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => {
      onClose();
    }, 5000);
    if (mouseEnter) {
      clearTimeout(id);
    }
    return () => {
      clearTimeout(id);
    };
  }, [mouseEnter, onClose]);

  /**
   * Scroll to top so that header will enter to viewport
   */
  useEffect(() => {
    if (isOpen && window) {
      window.scrollTo({
        behavior: 'smooth',
        top: 0,
      });
    }
  }, [isOpen]);

  /**
   * Reset size when window's size change and header state is changed
   */
  useEffect(() => {
    if (windowSize && iconRef.current) {
      setToolTipDom(iconRef.current.getBoundingClientRect());
    }
  }, [iconRef, windowSize, isHeaderHidden, throttledIsOpen]);

  /**
   * Check dom until it's available because on initial render, header ref is not available.
   */
  useInterval(
    () => {
      if (typeof window !== 'undefined' && iconRef.current) {
        setToolTipDom(iconRef.current.getBoundingClientRect());
        toggleIsRunning(false);
      }
    },
    isRunning ? 1000 : null
  );

  if (!toolTipDom) return null;

  return (
    <AnimatePresence>
      {throttledIsOpen && !isHeaderHidden && (
        <m.div
          className="z-navigation-tooltip bg-other-white relative w-[400px] overflow-hidden rounded-lg pt-3 shadow-lg"
          onMouseEnter={() => {
            setMouseEnter(true);
          }}
          style={{
            position: 'fixed',
            top: toolTipDom.top + toolTipDom.height + 15,
            left: toolTipDom.left - toolTipDom.width / 2 - 300,
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: isHeaderHidden ? 0 : 1 }}
          exit={{
            opacity: 0,
          }}
          transition={{ duration: 0.5, ease: 'easeInOut' }}
        >
          <div
            className="bg-other-white fixed top-[-10px] h-5 w-5 rotate-45"
            style={{
              left: toolTipDom.left + toolTipDom.width / 3,
              top: toolTipDom.top + toolTipDom.height + 8,
            }}
          />
          {children}
        </m.div>
      )}
    </AnimatePresence>
  );
};

export default WrapperDesktop;
